import { memo, useCallback, useEffect, useId, useRef, useState } from 'react';
import type { ComponentType, CSSProperties } from 'react';
import { StoneSkeleton } from '@/components/media';
import { useQuality } from '@/lib/quality';
import { CompareDivider } from './CompareDivider';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from './geometry';
import { PhotoBack, PhotoFront, PhotoShade } from './scenes/photo';
import { regionsFor, unionClipPath } from './scenes/regions';
import type { ArtProps, SceneDefinition } from './scenes/types';
import { PlaneSet } from './StonePlanes';
import { useSpecularSpot } from './useSpecularSpot';
import { useStageScale } from './useStageScale';

export interface StageTexture {
  /** Changes when the stone changes (the cross-fade keys on it). */
  key: string;
  /** A URL a CSS `background-image` can use. */
  url: string;
}

/** `loading`: no texture yet · `ready`: a catalogue stone · `sample`: the built-in stone (catalogue unreachable). */
export type StageStatus = 'loading' | 'ready' | 'sample';

export interface StageCopy {
  compareLabel: string;
  compareText: (percent: number) => string;
  stoneChip: string;
  plainChip: string;
}

export interface StageProps {
  scene: SceneDefinition;
  status: StageStatus;
  texture: StageTexture | null;
  scale: number;
  rotate: 0 | 90;
  seams: boolean;
  /** 0 – 100: how much of the stage, from the left, shows the stone. */
  compare: number;
  onCompare: (value: number, commit: boolean) => void;
  /** The picture's accessible name — built by the page so it can say what is on screen. */
  label: string;
  copy: StageCopy;
}

const VIEW_BOX = `0 0 ${String(DESIGN_WIDTH)} ${String(DESIGN_HEIGHT)}`;

interface ArtLayerProps {
  Art: ComponentType<ArtProps>;
  ids: string;
  className: string;
}

/** One SVG layer of drawn art. Memoised: the stage re-renders on every drag frame, the art never changes. */
const ArtLayer = memo(function ArtLayer({ Art, ids, className }: ArtLayerProps) {
  return (
    <svg
      className={className}
      viewBox={VIEW_BOX}
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <Art ids={ids} />
    </svg>
  );
});

interface TextureLayer {
  key: string;
  url: string;
  /** Fade in over the layer below (a swap) rather than appear (the first stone). */
  fade: boolean;
}

/** How long a replaced texture stays underneath while the new one fades in. */
const SWAP_MS = 520;

/**
 * The picture: a fixed 3 : 2 frame in which the scene is stacked from back to front.
 *
 *   back art (SVG) → plain matte planes → [ stone planes + polished highlights ] → shadows → front art
 *
 * The bracketed group is the "after" half of the before/after comparison: it is revealed from the left
 * edge to the divider by translating a clipping wrapper and counter-translating its content — two
 * transforms, no repaint — so dragging stays smooth. The picture itself is a single `role="img"` with a
 * live label; the divider is its sibling, because an image's children are invisible to assistive technology.
 */
export function Stage({
  scene,
  status,
  texture,
  scale,
  rotate,
  seams,
  compare,
  onCompare,
  label,
  copy,
}: StageProps) {
  const tier = useQuality();
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const [measure, k] = useStageScale();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const spotRef = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState(false);

  // Texture stack: a swap keeps the old stone underneath while the new one fades in, then drops it.
  const [layers, setLayers] = useState<TextureLayer[]>([]);
  if (texture && layers.at(-1)?.key !== texture.key) {
    setLayers((previous) => [
      ...previous.slice(-1),
      { key: texture.key, url: texture.url, fade: previous.length > 0 && tier !== 'minimal' },
    ]);
  }
  useEffect(() => {
    if (layers.length < 2) return undefined;
    const timer = window.setTimeout(() => {
      setLayers((previous) => previous.slice(-1));
    }, SWAP_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [layers]);

  useSpecularSpot(rootRef, spotRef, tier === 'full' && k !== null);

  const regions = regionsFor(scene);
  const photo = scene.photo;
  const percent = Math.round(compare);
  const unionId = `${uid}-union`;

  const setRoot = useCallback(
    (element: HTMLDivElement | null): void | (() => void) => {
      rootRef.current = element;
      return measure(element);
    },
    [measure],
  );

  return (
    <div
      ref={setRoot}
      className="viz-stage on-dark"
      data-state={status}
      data-dragging={dragging || undefined}
      data-edge={percent <= 0 || percent >= 100 || undefined}
      style={{ '--cmp': compare } as CSSProperties}
      aria-busy={status === 'loading' || undefined}
    >
      <div className="viz-stage__art" role="img" aria-label={label}>
        {k === null ? (
          <StoneSkeleton className="viz-stage__skeleton" />
        ) : (
          <>
            {photo ? (
              <svg
                className="viz-layer viz-back"
                viewBox={VIEW_BOX}
                preserveAspectRatio="none"
                aria-hidden="true"
                focusable="false"
              >
                <PhotoBack photo={photo} />
              </svg>
            ) : (
              <ArtLayer Art={scene.Back} ids={uid} className="viz-layer viz-back" />
            )}

            <div className="viz-planes" aria-hidden="true">
              <PlaneSet regions={regions} k={k} variant="matte" />
            </div>

            <div className="viz-reveal" aria-hidden="true">
              <div className="viz-reveal__inner">
                <div className="viz-planes">
                  {layers.length === 0 ? (
                    <PlaneSet
                      regions={regions}
                      k={k}
                      variant="loading"
                      scale={scale}
                      rotate={rotate}
                    />
                  ) : (
                    layers.map((layer) => (
                      <div
                        key={layer.key}
                        className="viz-planes"
                        data-fade={layer.fade || undefined}
                      >
                        <PlaneSet
                          regions={regions}
                          k={k}
                          variant="stone"
                          url={layer.url}
                          scale={scale}
                          rotate={rotate}
                          seams={seams}
                        />
                      </div>
                    ))
                  )}
                </div>
                <div className="viz-sheen" style={{ clipPath: `url(#${unionId})` }}>
                  <svg
                    className="viz-layer"
                    viewBox={VIEW_BOX}
                    preserveAspectRatio="none"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <defs>
                      <clipPath id={unionId} clipPathUnits="objectBoundingBox">
                        <path d={unionClipPath(regions)} />
                      </clipPath>
                    </defs>
                    {photo ? null : <scene.Sheen ids={uid} />}
                  </svg>
                  <div ref={spotRef} className="viz-spot" />
                </div>
              </div>
            </div>

            {photo ? (
              <svg
                className="viz-layer viz-shade"
                viewBox={VIEW_BOX}
                preserveAspectRatio="none"
                aria-hidden="true"
                focusable="false"
              >
                <PhotoShade ids={uid} regions={regions} />
              </svg>
            ) : (
              <ArtLayer Art={scene.Shade} ids={uid} className="viz-layer viz-shade" />
            )}

            {photo ? (
              <svg
                className="viz-layer viz-front"
                viewBox={VIEW_BOX}
                preserveAspectRatio="none"
                aria-hidden="true"
                focusable="false"
              >
                <PhotoFront photo={photo} />
              </svg>
            ) : (
              <ArtLayer Art={scene.Front} ids={uid} className="viz-layer viz-front" />
            )}
          </>
        )}
      </div>

      <div className="viz-chips" aria-hidden="true">
        <span className="viz-chip" data-side="stone" data-hidden={percent < 24 || undefined}>
          {copy.stoneChip}
        </span>
        <span className="viz-chip" data-side="plain" data-hidden={percent > 76 || undefined}>
          {copy.plainChip}
        </span>
      </div>

      <div className="viz-divider-track">
        <span className="viz-divider-line" aria-hidden="true" />
        <CompareDivider
          container={rootRef}
          value={compare}
          onChange={onCompare}
          label={copy.compareLabel}
          valueText={copy.compareText(percent)}
          onDraggingChange={setDragging}
        />
      </div>
    </div>
  );
}
